import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Label, Popover, PopoverContent, PopoverTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCheckbox, SpecraFormField, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsUpDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTenantAreas } from '../../../../lib/access/useTenantAreas';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { formatGovernmentOrgLabel, useAddGovernmentOffice, useGovernmentOrgAncestorChain, useGovernmentOrganizations, useGovernmentOrgCatalog } from '../../Programs/referenceDataHooks';
import { companyRoleLabel } from '../constants/companyRoleTypes';
const LEVEL_LABELS = [
    'Department / agency',
    'Sub-agency',
    'Office',
    'Sub-office'
];
export function GovernmentOrgPicker({ id, label, helpText, value, onChange, sameAsOrgId, testid }) {
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sameAsContracting, setSameAsContracting] = useState(false);
    const [levelIds, setLevelIds] = useState([]);
    const catalog = useGovernmentOrgCatalog();
    const ancestorChain = useGovernmentOrgAncestorChain(value, catalog.byId);
    useEffect(()=>{
        if (!value || ancestorChain.length === 0) {
            return;
        }
        setLevelIds(ancestorChain.map((o)=>o.id));
    }, [
        value,
        ancestorChain
    ]);
    useEffect(()=>{
        if (sameAsContracting && sameAsOrgId) {
            onChange(sameAsOrgId);
        }
    }, [
        sameAsContracting,
        sameAsOrgId,
        onChange
    ]);
    const searchEnabled = searchQuery.trim().length >= 2;
    const searchResults = useGovernmentOrganizations({
        search: searchQuery,
        enabled: searchEnabled
    });
    const roots = useGovernmentOrganizations({
        rootsOnly: true
    });
    const selectedOrg = value ? catalog.byId.get(value) : undefined;
    const deepest = catalog.byId.get(levelIds[levelIds.length - 1] ?? '');
    const triggerLabel = selectedOrg?.displayPath || (selectedOrg ? formatGovernmentOrgLabel(selectedOrg) : 'Search or browse…');
    const handleSearchSelect = (org)=>{
        onChange(org.id);
        setSearchOpen(false);
        setSearchQuery('');
    };
    const handleLevelChange = (levelIndex, orgId)=>{
        const next = levelIds.slice(0, levelIndex);
        next[levelIndex] = orgId;
        setLevelIds(next);
        onChange(orgId);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded-lg border border-border bg-muted/50 p-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: id,
                        className: "text-sm font-medium text-foreground",
                        children: label
                    }),
                    helpText && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: helpText
                    })
                ]
            }),
            sameAsOrgId !== undefined && /*#__PURE__*/ _jsxs("label", {
                className: "flex items-center gap-2 text-xs text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        checked: sameAsContracting,
                        onCheckedChange: (c)=>setSameAsContracting(Boolean(c)),
                        disabled: !sameAsOrgId
                    }),
                    "Same as contracting office"
                ]
            }),
            /*#__PURE__*/ _jsxs(Popover, {
                open: searchOpen,
                onOpenChange: setSearchOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            id: id,
                            type: "button",
                            variant: "secondary",
                            role: "combobox",
                            "aria-expanded": searchOpen,
                            disabled: sameAsContracting,
                            className: "w-full justify-between bg-background font-normal",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate text-left",
                                    children: triggerLabel
                                }),
                                /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                    className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        className: "w-[var(--radix-popover-trigger-width)] p-0",
                        align: "start",
                        children: /*#__PURE__*/ _jsxs(Command, {
                            label: `Search ${label}`,
                            shouldFilter: false,
                            children: [
                                /*#__PURE__*/ _jsx(CommandInput, {
                                    "data-testid": testid,
                                    placeholder: "Search by name or acronym…",
                                    value: searchQuery,
                                    onValueChange: setSearchQuery
                                }),
                                /*#__PURE__*/ _jsxs(CommandList, {
                                    children: [
                                        !searchEnabled && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs text-muted-foreground",
                                            children: "Type at least 2 characters to search"
                                        }),
                                        searchEnabled && searchResults.isLoading && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs",
                                            children: "Searching…"
                                        }),
                                        searchEnabled && !searchResults.isLoading && (searchResults.data?.length ?? 0) === 0 && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs",
                                            children: "No organizations found"
                                        }),
                                        searchEnabled && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: (searchResults.data ?? []).map((org)=>/*#__PURE__*/ _jsxs(CommandItem, {
                                                    value: org.id,
                                                    onSelect: ()=>handleSearchSelect(org),
                                                    className: "flex flex-col items-start gap-0.5 py-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: formatGovernmentOrgLabel(org)
                                                        }),
                                                        org.displayPath && /*#__PURE__*/ _jsx("span", {
                                                            className: "text-[11px] text-muted-foreground",
                                                            children: org.displayPath
                                                        })
                                                    ]
                                                }, org.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 pt-1",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
                        children: "Browse hierarchy"
                    }),
                    /*#__PURE__*/ _jsx(HierarchyLevels, {
                        levelIds: levelIds,
                        onLevelChange: handleLevelChange,
                        roots: roots.data ?? [],
                        disabled: sameAsContracting,
                        byId: catalog.byId
                    })
                ]
            }),
            deepest ? /*#__PURE__*/ _jsx(AddOffice, {
                parent: deepest,
                disabled: sameAsContracting,
                onAdded: (org)=>{
                    setLevelIds([
                        ...levelIds,
                        org.id
                    ]);
                    onChange(org.id);
                }
            }) : null
        ]
    });
}
function AddOffice({ parent, disabled, onAdded }) {
    // Government orgs are company settings (SAD-948): admin, internal or the
    // Company Settings role may add an office; ryugu re-checks the same grant.
    const canAdd = useTenantAreas().canManageCompanySettings;
    const add = useAddGovernmentOffice();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [acronym, setAcronym] = useState('');
    if (!canAdd) return null;
    const parentLabel = formatGovernmentOrgLabel(parent);
    if (!open) {
        return /*#__PURE__*/ _jsxs(SpecraButton, {
            type: "button",
            size: "sm",
            variant: "ghost",
            disabled: disabled,
            onClick: ()=>setOpen(true),
            "data-testid": Testids.deliveryShared.govOrgAddOffice,
            children: [
                "+ Add office under ",
                parentLabel
            ]
        });
    }
    const save = ()=>add.mutate({
            parent,
            name,
            acronym
        }, {
            onSuccess: (org)=>{
                if (!org) return;
                toast.success(`Added ${formatGovernmentOrgLabel(org)}`);
                setOpen(false);
                setName('');
                setAcronym('');
                onAdded(org);
            },
            onError: (err)=>toast.error(apiErrorMessage(err, 'Could not add the office'))
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded-md border border-border bg-background p-2",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "New office under ",
                    parentLabel
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid gap-2 sm:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Office name",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: name,
                            onChange: (e)=>setName(e.target.value),
                            maxLength: 255,
                            "data-testid": Testids.deliveryShared.govOrgAddOfficeName
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Acronym",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: acronym,
                            onChange: (e)=>setAcronym(e.target.value),
                            maxLength: 50,
                            "data-testid": Testids.deliveryShared.govOrgAddOfficeAcronym
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        disabled: !name.trim() || add.isPending,
                        onClick: save,
                        "data-testid": Testids.deliveryShared.govOrgAddOfficeSave,
                        children: add.isPending ? 'Adding…' : 'Add office'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "secondary",
                        onClick: ()=>setOpen(false),
                        children: "Cancel"
                    })
                ]
            })
        ]
    });
}
function HierarchyLevels({ levelIds, onLevelChange, roots, disabled, byId }) {
    const levelsToShow = Math.max(1, levelIds.length + 1, 2);
    const capped = Math.min(levelsToShow, 4);
    return /*#__PURE__*/ _jsx("div", {
        className: "grid gap-2 sm:grid-cols-2",
        children: Array.from({
            length: capped
        }, (_, levelIndex)=>{
            const parentId = levelIndex === 0 ? '' : levelIds[levelIndex - 1];
            return /*#__PURE__*/ _jsx(HierarchySelect, {
                levelIndex: levelIndex,
                label: LEVEL_LABELS[levelIndex] ?? `Level ${levelIndex + 1}`,
                parentId: parentId,
                value: levelIds[levelIndex] ?? '',
                roots: levelIndex === 0 ? roots : undefined,
                disabled: disabled || levelIndex > 0 && !parentId,
                onChange: (id)=>onLevelChange(levelIndex, id),
                byId: byId
            }, levelIndex);
        })
    });
}
function HierarchySelect({ levelIndex, label, parentId, value, roots, disabled, onChange, byId }) {
    const children = useGovernmentOrganizations({
        parentId,
        enabled: levelIndex > 0 && !!parentId
    });
    const options = levelIndex === 0 ? roots ?? [] : children.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                className: "text-xs text-muted-foreground",
                children: label
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: value,
                onValueChange: onChange,
                disabled: disabled,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        className: "h-9 bg-background text-sm",
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: disabled ? 'Select parent first' : 'Select…'
                        })
                    }),
                    /*#__PURE__*/ _jsx(SelectContent, {
                        children: options.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                value: o.id,
                                children: formatGovernmentOrgLabel(o)
                            }, o.id))
                    })
                ]
            }),
            value && byId.get(value)?.displayPath && /*#__PURE__*/ _jsx("p", {
                className: "text-[10px] text-muted-foreground line-clamp-2",
                children: byId.get(value)?.displayPath
            })
        ]
    });
}
/** Renders contracting + customer org pickers for program forms (SDS dual-FK). */ export function ProgramGovernmentOrgFields({ governmentOrganizationId, customerOrgId, onGovernmentOrganizationIdChange, onCustomerOrgIdChange }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
                id: "governmentOrganizationId",
                label: "Contracting office",
                helpText: "Acquisition authority for this program (e.g. TAC for RES).",
                value: governmentOrganizationId,
                onChange: onGovernmentOrganizationIdChange,
                testid: Testids.deliveryShared.govOrgSearchContracting
            }),
            /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
                id: "customerOrgId",
                label: "Customer / mission office",
                helpText: "Program beneficiary office (e.g. VR&E for RES).",
                value: customerOrgId,
                onChange: onCustomerOrgIdChange,
                sameAsOrgId: governmentOrganizationId,
                testid: Testids.deliveryShared.govOrgSearchCustomer
            })
        ]
    });
}
export function programOrgDisplay(p) {
    return {
        pricing: p.pricingType || '—',
        state: p.operationalStateLabel || '—',
        customer: p.customerOrgName || '—',
        contracting: p.governmentOrganizationName || '—',
        cabinet: p.cabinetName || '—',
        companyRole: companyRoleLabel(p.companyRoleType),
        prime: p.primeContractorCompanyName || '—'
    };
}
